import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Button } from 'reactstrap';
import Select from 'react-select';
import { getVeloxityCsvInfo } from '../../../slices/veloxity/audience/CsvInfoReducer';
import { showErrorAlert, showSuccessAlert } from '../../custom/sweetalertHelper';
import Draggable from 'react-draggable';

const AudienceCsvDownloadModal = ({ isOpen, toggle, selectedAudience, updateLoading }) => {
    const dispatch = useDispatch();
    const [selectedModuleTypes, setSelectedModuleTypes] = useState([]);
    const [fetchedModuleTypes, setFetchedModuleTypes] = useState([]);
    const [urls, setUrls] = useState({}); // Store the URLs here

    const modifyLabel = (label) => {
        return label
            .toLowerCase()
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    useEffect(() => {
        if (isOpen && selectedAudience.length) {
            dispatch(getVeloxityCsvInfo({ audienceId: selectedAudience[0] }))
                .then((action) => {
                    if (action.type.endsWith('fulfilled')) {
                        const { moduleTypes, urls } = action.payload; 
                        const formattedModuleTypes = moduleTypes.map(type => ({
                            value: type,
                            label: modifyLabel(type)
                        }));
                        setFetchedModuleTypes(formattedModuleTypes);
                        setUrls(urls);
                    } else {
                        showErrorAlert('Error', 'Failed to fetch module types and URLs');
                    }
                })
                .catch(error => console.error('An error occurred:', error));
        }
    }, [isOpen, selectedAudience, dispatch]);

    const handleModuleTypeChange = (selectedOptions) => {
        const allOption = selectedOptions.find(option => option.value === 'ALL');
        if (allOption) {
            setSelectedModuleTypes(fetchedModuleTypes);
        } else {
            setSelectedModuleTypes(selectedOptions || []);
        }
    };

    const downloadCsvFile = async (url, filename) => {
        return new Promise((resolve) => {
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${filename}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => resolve(), 1000);
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (selectedModuleTypes.length === 0) {
            showErrorAlert('Validation Error', 'Please select at least one module type.');
            return;
        }
        updateLoading(true);

        try {

            for (const type of selectedModuleTypes) {
                const url = urls[type.value];
                if (url) {
                    await downloadCsvFile(url, type.label);
                }
            }

            showSuccessAlert('Success!', 'Your data files are being downloaded!');
        } catch (error) {
            console.error('An error occurred:', error);
            showErrorAlert('Error!', 'Unable to download CSV files.');
        } finally {
            updateLoading(false);
            toggle();
        }
    };

    return (
        <Draggable handle=".modal-header">
            <Modal isOpen={isOpen} toggle={toggle} className="modal-dialog-centered">
                <ModalHeader toggle={toggle}>Download Data File</ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleSubmit}>
                        <FormGroup>
                            <Label for="moduleTypes">Module Types</Label>
                            <div style={{ width: '105%', margin: '0 -2.5%' }}>
                                <Select
                                    id="moduleTypes"
                                    name="moduleTypes"
                                    isMulti
                                    options={[{ value: 'ALL', label: 'All Modules' }, ...fetchedModuleTypes]}
                                    value={selectedModuleTypes}
                                    onChange={handleModuleTypeChange}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    closeMenuOnSelect={false}
                                />
                            </div>
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle}>Close</Button>
                    <Button color="primary" onClick={handleSubmit}>Download Data File</Button>
                </ModalFooter>
            </Modal>
        </Draggable>
    );
};

export default AudienceCsvDownloadModal;
